import React from "react";

/**
 * Live design reference for the **primary** site shell (Work, About, /for/…, CompDesign routes).
 * Individual **project** pages may vary in layout; reuse these tokens and components here so new
 * project pages still feel like the same brand.
 *
 * @see tailwind.config.js — `content`, `border`, `fontFamily.sans` / `mono`
 * @see components/layout/Header.js, Footer.js — components/home/ProjectRail.js — pages/primary/*
 */
const DesignSystemPage = () => {
  return (
    <div
      className="
        min-h-screen bg-white dark:bg-black text-black dark:text-white
        transition-colors duration-500
        px-6 lg:px-12 pt-24 pb-20
        max-w-[1440px] mx-auto
      "
    >
      <header className="mb-14 lg:mb-16 border-b border-border-subtle dark:border-neutral-800 pb-8">
        <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted">
          Dev · /design-system
        </p>
        <p className="mt-2 font-mono text-meta uppercase tracking-[0.14em] text-content-muted">
          Project case blocks ·{" "}
          <a href="/#/project-blocks" className="underline underline-offset-4 hover:text-black dark:hover:text-white">
            /project-blocks
          </a>
        </p>
        <h1 className="mt-4 text-section font-medium tracking-tight">
          Primary site design system
        </h1>
        <p className="mt-4 max-w-[60ch] text-body font-light leading-relaxed text-content-secondary dark:text-neutral-400">
          Typography, spacing, and UI blocks copied from the public shell — not from individual
          project case studies. Use this page when building or refactoring{" "}
          <code className="font-mono text-meta text-content-muted">pages/projects/*</code>.
        </p>
      </header>

      {/* Page shell */}
      <Section kicker="Layout" title="Primary page shell">
        <p className="mb-6 max-w-[65ch] text-body font-light text-content-secondary dark:text-neutral-400">
          Every primary <code className="font-mono text-meta">main</code> uses{" "}
          <strong className="font-normal text-black dark:text-white">72px top offset</strong> for the
          fixed header, full-bleed background, and (for horizontal banding){" "}
          <strong className="font-normal text-black dark:text-white">max-width 1440px</strong> with{" "}
          <strong className="font-normal text-black dark:text-white">px-6 lg:px-12</strong> — same as{" "}
          Header and Footer gutters.
        </p>
        <pre className="overflow-x-auto border border-border dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950 p-4 font-mono text-meta leading-relaxed text-content-secondary dark:text-neutral-400">
          {`<main className="flex flex-1 flex-col min-h-0 w-full bg-white dark:bg-black pt-[72px] box-border transition-colors duration-500">
  <div className="max-w-[1440px] mx-auto w-full px-6 lg:px-12 py-4 lg:py-6">
    …
  </div>
</main>`}
        </pre>
      </Section>

      {/* Colors */}
      <Section kicker="Tokens" title="Colour (Tailwind)">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Swatch
            label="Ink / headline"
            className="bg-black dark:bg-white"
            caption="text-black dark:text-white"
          />
          <Swatch
            label="Secondary copy"
            border
            className="bg-content-secondary dark:bg-neutral-400"
            caption="text-content-secondary dark:text-neutral-400"
          />
          <Swatch
            label="Muted / labels"
            border
            className="bg-content-muted dark:bg-neutral-500"
            caption="text-content-muted dark:text-neutral-500"
          />
          <Swatch
            label="Border"
            border
            className="bg-border dark:bg-neutral-800"
            caption="border-border · dark:border-neutral-800"
          />
          <Swatch
            label="Border subtle"
            border
            className="bg-[#F0F0F0] dark:bg-neutral-900"
            caption="border-border-subtle"
          />
          <Swatch
            label="Tile / figure bg"
            border
            className="bg-[#F5F5F5] dark:bg-[#141414]"
            caption="LandingGrid tile idle (see LandingGrid.js)"
          />
        </div>
      </Section>

      {/* Typography */}
      <Section kicker="Typography" title="Sans · mono">
        <div className="space-y-10 border border-border dark:border-neutral-800 p-6 lg:p-8">
          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-3">
              Nav & brand (Header)
            </p>
            <p className="font-sans text-body font-medium uppercase tracking-[0.18em] text-black dark:text-white">
              Shareef Jasim · work · about
            </p>
          </div>

          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-3">
              Filter pills (home only)
            </p>
            <p className="text-body font-light tracking-wide text-black dark:text-white">All</p>
            <p className="text-body font-light tracking-wide text-content-muted dark:text-neutral-500">
              Computational
            </p>
          </div>

          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-3">
              About H1
            </p>
            <h2 className="text-section font-medium tracking-tight text-black dark:text-white">
              <span className="text-black dark:text-white">Hi, I&apos;m Shareef,</span>{" "}
              <span className="text-content-muted dark:text-neutral-500 font-light">
                computational designer &amp; creative technologist.
              </span>
            </h2>
          </div>

          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-3">
              Private pitch H1
            </p>
            <h2 className="text-section font-medium tracking-tight text-black dark:text-white">
              Work selected for your team.
            </h2>
          </div>

          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-3">
              Rail title (ProjectRail)
            </p>
            <h2 className="text-section font-medium tracking-tight">
              <span className="text-black dark:text-white">The Cutaway</span>
              <span className="text-black dark:text-white">, </span>
              <span className="text-content-muted dark:text-neutral-500 font-light">
                community space.
              </span>
            </h2>
          </div>

          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-3">
              Body (About / rail / private)
            </p>
            <p className="max-w-[60ch] text-body text-content-secondary dark:text-neutral-400 font-light leading-relaxed">
              Single-column reading: comfortable line length with{" "}
              <code className="font-mono text-meta">max-w-[60ch]</code> or{" "}
              <code className="font-mono text-meta">max-w-[34ch]</code> in the narrow rail.
            </p>
          </div>

          <div>
            <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-4">
              Mono ladder (labels · meta · footer)
            </p>
            <ul className="space-y-2 font-mono text-meta uppercase tracking-[0.18em] text-content-muted">
              <li>Kickers · rail index · footer — text-meta (~11px)</li>
              <li>Empty rail prompt — text-meta</li>
              <li className="font-sans normal-case text-body tracking-normal text-content-secondary">
                Body copy uses text-body (~15px), not mono.
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Buttons */}
      <Section kicker="Actions" title="Buttons & links">
        <p className="mb-6 max-w-[65ch] text-body font-light text-content-secondary dark:text-neutral-400">
          Primary actions use <strong className="font-normal text-black dark:text-white">filled</strong>{" "}
          black (inverse in dark). Secondary actions use{" "}
          <strong className="font-normal text-black dark:text-white">outline + hover invert</strong>.
          Add <code className="font-mono text-meta">data-cursor="pointer"</code> and{" "}
          <code className="font-mono text-meta">clickable cursor-none</code> for the square cursor.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className="
              inline-flex items-center justify-between gap-3
              px-4 py-[10px]
              bg-black text-white dark:bg-white dark:text-black
              font-mono text-meta tracking-[0.18em] uppercase
              border border-black dark:border-white
            "
          >
            Primary <span aria-hidden="true">&darr;</span>
          </span>
          <span
            className="
              inline-flex items-center justify-between gap-3
              px-4 py-[10px]
              border border-black dark:border-white
              font-mono text-meta tracking-[0.18em] uppercase
              text-black dark:text-white
            "
          >
            Secondary <span aria-hidden="true">↗</span>
          </span>
          <span
            className="
              inline-flex items-center justify-between gap-4
              min-w-[8.5rem] px-4 py-3
              border border-black dark:border-white
              font-mono text-meta tracking-[0.18em] uppercase text-black dark:text-white
            "
          >
            Open <span aria-hidden="true">&rarr;</span>
          </span>
        </div>
      </Section>

      {/* Home layout */}
      <Section kicker="Work landing" title="Grid + rail">
        <ul className="list-disc pl-5 space-y-2 text-body font-light text-content-secondary dark:text-neutral-400 max-w-[65ch]">
          <li>
            Two columns from <code className="font-mono text-meta">lg</code>: gallery{" "}
            <code className="font-mono text-meta">flex-1</code>, right rail{" "}
            <code className="font-mono text-meta">w-[320px] max-w-[320px]</code>.
          </li>
          <li>
            Vertical separator:{" "}
            <code className="font-mono text-meta">
              lg:border-r lg:border-border dark:lg:border-neutral-800 lg:pr-8
            </code>
          </li>
          <li>
            Mobile: rail below grid with{" "}
            <code className="font-mono text-meta">border-t border-border-subtle</code>.
          </li>
          <li>
            Tiles: square cells, 1px outline, grayscale until active — see{" "}
            <code className="font-mono text-meta">LandingGrid.js</code>.
          </li>
          <li>
            Figure in rail: <code className="font-mono text-meta">aspect-square max-w-[280px]</code>,{" "}
            <code className="font-mono text-meta">object-contain</code>, hairline outline.
          </li>
        </ul>
      </Section>

      {/* Private badge */}
      <Section kicker="Private routes" title="Header badge (/for/…)">
        <div className="flex items-center gap-4">
          <span className="font-sans text-body font-medium uppercase tracking-[0.18em]">
            Shareef Jasim
          </span>
          <span
            className="
              inline-flex items-center justify-center
              px-2 pt-px h-[24px] sm:h-6
              font-mono text-meta uppercase tracking-[0.18em] leading-none
              bg-black text-white dark:bg-white dark:text-black
            "
          >
            Private
          </span>
        </div>
      </Section>

      {/* Section dividers */}
      <Section kicker="Sections" title="Dividers (e.g. private page)">
        <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-4">
          Relevant for the role · 5
        </p>
        <div className="h-24 border border-dashed border-border dark:border-neutral-700 flex items-center justify-center text-meta font-mono uppercase text-content-muted">
          Placeholder tiles
        </div>
        <div className="mt-10 pt-8 border-t border-border-subtle dark:border-neutral-800">
          <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-4">
            Additional projects · 7
          </p>
          <div className="h-24 border border-dashed border-border dark:border-neutral-700 flex items-center justify-center text-meta font-mono uppercase text-content-muted">
            Remaining tiles
          </div>
        </div>
      </Section>

      {/* Footer strip */}
      <Section kicker="Footer" title="Global footer">
        <div className="border border-border dark:border-neutral-800 bg-white dark:bg-black px-6 lg:px-12 py-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <p className="font-mono text-meta tracking-[0.18em] uppercase text-content-muted">
            Computational Designer&nbsp;&middot;&nbsp;Creative Technologist
          </p>
          <nav className="flex items-center gap-6 sm:gap-8">
            {["Resume", "LinkedIn", "GitHub", "Email"].map((label) => (
              <span
                key={label}
                className="font-mono text-meta tracking-[0.18em] uppercase text-content-muted"
              >
                {label}
              </span>
            ))}
          </nav>
        </div>
      </Section>

      {/* Checklist for project pages */}
      <section className="mt-16 lg:mt-20 border-t border-border-subtle dark:border-neutral-800 pt-10">
        <h2 className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted mb-4">
          Checklist for new project pages
        </h2>
        <ul className="list-disc pl-5 space-y-2 text-body font-light text-content-secondary dark:text-neutral-400 max-w-[65ch]">
          <li>Use Roboto (sans) and the mono stack for labels — no new display fonts.</li>
          <li>Keep corners square; avoid introducing rounded UI unless the rest of the shell changes.</li>
          <li>Prefer <code className="font-mono text-meta">text-content-*</code> and dark: neutrals over ad-hoc hex.</li>
          <li>
            Match header offset: project pages scroll under the fixed header unless wrapped in a
            shell — set <code className="font-mono text-meta">pt-[72px]</code> (or consistent top padding) on the scroll root.
          </li>
          <li>Use the same button classes as About / rail for “open”, “download”, external links.</li>
        </ul>
      </section>
    </div>
  );
};

function Section({ kicker, title, children }) {
  return (
    <section className="mb-14 lg:mb-16">
      <p className="font-mono text-meta uppercase tracking-[0.18em] text-content-muted">{kicker}</p>
      <h2 className="mt-2 text-lede font-medium tracking-tight text-black dark:text-white">
        {title}
      </h2>
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Swatch({ label, className, caption, border }) {
  return (
    <div
      className={`overflow-hidden ${
        border ? "ring-1 ring-border dark:ring-neutral-700" : ""
      }`}
    >
      <div className={`h-14 ${className}`} aria-hidden />
      <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-950 border-t border-border dark:border-neutral-800">
        <p className="text-body font-medium text-black dark:text-white">{label}</p>
        <p className="font-mono text-meta text-content-muted mt-1 leading-snug">{caption}</p>
      </div>
    </div>
  );
}

export default DesignSystemPage;
